import { useQuery } from '@tanstack/react-query';
import {
  Plane,
  Users,
  Boxes,
  ClipboardCheck,
  AlertTriangle,
  Clock,
  TrendingUp,
  ArrowUpRight,
  Loader2,
} from 'lucide-react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { apiGet } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { API_ROUTES, type DashboardData, type DashboardAlert, FLIGHT_STATUS_COLORS } from '@avgrp/shared';

// ─── Stat Card ──────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}

function StatCard({ title, value, subtitle, icon: Icon, iconColor, iconBg }: StatCardProps) {
  return (
    <div className="card flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
        <p className="mt-1 text-xs text-gray-400">{subtitle}</p>
      </div>
      <div className={`rounded-xl p-3 ${iconBg}`}>
        <Icon className={`h-6 w-6 ${iconColor}`} />
      </div>
    </div>
  );
}

// ─── Alert Item ─────────────────────────────────────────────

function AlertItem({ alert }: { alert: DashboardAlert }) {
  const iconMap: Record<string, React.ElementType> = {
    INCIDENT: AlertTriangle,
    FLIGHT_DELAY: Clock,
    SICKNESS: Users,
    TOOL_FAILURE: Boxes,
    TASK_OVERDUE: Clock,
    RESOURCE_SHORTAGE: Boxes,
    SHIFT_GAP: Users,
  };
  const Icon = iconMap[alert.type] || AlertTriangle;

  const colorMap: Record<string, string> = {
    INCIDENT: 'text-red-500 bg-red-50',
    FLIGHT_DELAY: 'text-amber-500 bg-amber-50',
    SICKNESS: 'text-blue-500 bg-blue-50',
    TOOL_FAILURE: 'text-orange-500 bg-orange-50',
    TASK_OVERDUE: 'text-amber-500 bg-amber-50',
    RESOURCE_SHORTAGE: 'text-red-500 bg-red-50',
    SHIFT_GAP: 'text-purple-500 bg-purple-50',
    SYSTEM: 'text-gray-500 bg-gray-50',
  };
  const colors = colorMap[alert.type] || 'text-gray-500 bg-gray-50';
  const [textColor, bgColor] = colors.split(' ');

  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 p-3 transition-colors hover:bg-gray-50">
      <div className={`rounded-lg p-2 ${bgColor}`}>
        <Icon className={`h-4 w-4 ${textColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{alert.title}</p>
        <p className="mt-0.5 text-xs text-gray-500">{alert.message}</p>
        <p className="mt-1 text-2xs text-gray-400">
          {dayjs(alert.timestamp).format('HH:mm')}
        </p>
      </div>
    </div>
  );
}

// ─── Active Flight Board Row ────────────────────────────────

interface ActiveFlight {
  id: string;
  flightNumber: string;
  status: string;
  direction: string;
  origin: { iataCode: string; name: string };
  destination: { iataCode: string; name: string };
  aircraft: { registration: string; aircraftType: { name: string } };
  std: string;
  gate: string | null;
  taskProgress: { total: number; completed: number; rate: number };
}

function FlightBoardRow({ flight }: { flight: ActiveFlight }) {
  const statusColor = FLIGHT_STATUS_COLORS[flight.status] || '#6B7280';

  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="table-cell font-mono font-medium">{flight.flightNumber}</td>
      <td className="table-cell text-gray-500">{flight.aircraft.aircraftType.name}</td>
      <td className="table-cell">
        {flight.direction === 'DEPARTURE' ? flight.destination.iataCode : flight.origin.iataCode}
      </td>
      <td className="table-cell font-mono">{dayjs(flight.std).format('HH:mm')}</td>
      <td className="table-cell">
        <span
          className="badge"
          style={{ backgroundColor: `${statusColor}15`, color: statusColor }}
        >
          {flight.status.replace('_', ' ')}
        </span>
      </td>
      <td className="table-cell font-mono text-gray-500">{flight.gate || '-'}</td>
    </tr>
  );
}

// ─── Default empty dashboard ────────────────────────────────

const emptyDashboard: DashboardData = {
  flightsTotal: 0,
  flightsActive: 0,
  flightsDelayed: 0,
  flightsCompleted: 0,
  employeesOnShift: 0,
  employeesAvailable: 0,
  materialsInUse: 0,
  materialsAvailable: 0,
  tasksCompleted: 0,
  tasksPending: 0,
  alerts: [],
};

// ─── Dashboard Page ─────────────────────────────────────────

export function DashboardPage() {
  const { user } = useAuth();

  const { data: dashboard, isLoading: isDashLoading } = useQuery({
    queryKey: ['dashboard', user?.baseId],
    queryFn: () =>
      apiGet<{ data: DashboardData }>(API_ROUTES.DASHBOARD, {
        params: { baseId: user?.baseId, date: dayjs().format('YYYY-MM-DD') },
      }),
    select: (res) => res.data,
    enabled: !!user?.baseId,
  });

  const { data: activeFlights, isLoading: isFlightsLoading } = useQuery({
    queryKey: ['dashboard', 'active-flights', user?.baseId],
    queryFn: () =>
      apiGet<{ data: ActiveFlight[] }>(`${API_ROUTES.DASHBOARD}/active-flights`, {
        params: { baseId: user?.baseId },
      }),
    select: (res) => res.data,
    enabled: !!user?.baseId,
  });

  const stats = dashboard ?? emptyDashboard;
  const flightList = activeFlights ?? [];
  const isLoading = isDashLoading || isFlightsLoading;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of today&apos;s ground operations
        </p>
      </div>

      {!user?.baseId ? (
        <div className="card flex flex-col items-center justify-center py-20">
          <AlertTriangle className="mb-3 h-12 w-12 text-amber-300" />
          <p className="text-sm font-medium text-gray-700">No base assigned</p>
          <p className="mt-1 text-xs text-gray-400">Your account is not assigned to a base. Contact an administrator.</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              title="Active Flights"
              value={stats.flightsActive}
              subtitle={`${stats.flightsDelayed} delayed / ${stats.flightsTotal} total`}
              icon={Plane}
              iconColor="text-aviation-600"
              iconBg="bg-aviation-50"
            />
            <StatCard
              title="Employees on Shift"
              value={stats.employeesOnShift}
              subtitle={`${stats.employeesAvailable} available`}
              icon={Users}
              iconColor="text-emerald-600"
              iconBg="bg-emerald-50"
            />
            <StatCard
              title="Materials in Use"
              value={stats.materialsInUse}
              subtitle={`${stats.materialsAvailable} available`}
              icon={Boxes}
              iconColor="text-amber-600"
              iconBg="bg-amber-50"
            />
            <StatCard
              title="Tasks Completed"
              value={stats.tasksCompleted}
              subtitle={`${stats.tasksPending} pending`}
              icon={ClipboardCheck}
              iconColor="text-purple-600"
              iconBg="bg-purple-50"
            />
          </div>

          {/* Content grid: Flight Board + Alerts */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            {/* Flight Board Preview */}
            <div className="card col-span-1 xl:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Active Flights</h2>
                <Link
                  to="/flights"
                  className="flex items-center gap-1 text-sm text-aviation-600 hover:text-aviation-700"
                >
                  View all
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-header">Flight</th>
                      <th className="table-header">Aircraft</th>
                      <th className="table-header">Destination</th>
                      <th className="table-header">Time</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Gate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flightList.map((flight) => (
                      <FlightBoardRow key={flight.id} flight={flight} />
                    ))}
                    {flightList.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-gray-400">
                          No active flights
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Alerts Feed */}
            <div className="card col-span-1">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
                <span className="badge bg-red-50 text-red-700">
                  {stats.alerts.length} active
                </span>
              </div>
              <div className="space-y-3">
                {stats.alerts.map((alert) => (
                  <AlertItem key={alert.id} alert={alert} />
                ))}
                {stats.alerts.length === 0 && (
                  <p className="py-8 text-center text-sm text-gray-400">
                    No active alerts
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Quick stats bar */}
          <div className="card flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <TrendingUp className="h-4 w-4" />
              <span>Today&apos;s completion rate</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="h-2 w-48 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{
                    width: `${stats.flightsTotal > 0 ? Math.round((stats.flightsCompleted / stats.flightsTotal) * 100) : 0}%`,
                  }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-700">
                {stats.flightsTotal > 0
                  ? Math.round((stats.flightsCompleted / stats.flightsTotal) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
